import { useMemo } from "react";
import styled from "styled-components";
import StockIssueProjectCard from "./StockIssueProjectCard";
import {
  COLORS,
  FONTSIZE,
  FONTWEIGHT,
} from "helpers/designConstants";

const byName = (a, b) =>
  (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" });

function groupByDistributor(projects) {
  const map = new Map();
  for (const p of projects) {
    const key = p.distributor || "(no distributor)";
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(p);
  }

  return Array.from(map.entries())
    .map(([distributor, groupProjects]) => {
      const critical = groupProjects.reduce(
        (sum, p) =>
          sum + p.issues.filter((i) => i.severity === "critical").length,
        0,
      );
      const warning = groupProjects.reduce(
        (sum, p) =>
          sum + p.issues.filter((i) => i.severity === "warning").length,
        0,
      );
      // Projects inside a group: alphabetical by project name (case-insensitive).
      // Matches the group sort so the rep's mental model is consistent
      // everywhere and nothing reorders when severity changes.
      const sortedProjects = [...groupProjects].sort(byName);
      return {
        distributor,
        projects: sortedProjects,
        critical,
        warning,
      };
    })
    .sort((a, b) =>
      a.distributor.localeCompare(b.distributor, undefined, {
        sensitivity: "base",
      }),
    );
}

export default function StockIssuesGroupedList({
  projects,
  productsById,
  onUpdateStatus,
  onBatchUpdate,
}) {
  const groups = useMemo(() => groupByDistributor(projects), [projects]);

  return (
    <>
      {groups.map((group) => (
        <DistributorGroup
          key={group.distributor}
          group={group}
          productsById={productsById}
          onUpdateStatus={onUpdateStatus}
          onBatchUpdate={onBatchUpdate}
        />
      ))}
    </>
  );
}

function DistributorGroup({ group, productsById, onUpdateStatus, onBatchUpdate }) {
  const projectWord = group.projects.length === 1 ? "project" : "projects";

  return (
    <GroupWrapper>
      <GroupHeader>
        <DistributorName>{group.distributor}</DistributorName>
        <Dot>·</Dot>
        <Aggregates>
          {group.critical > 0 && (
            <>
              <CountNumber tone="critical">{group.critical}</CountNumber>
              <AggregateLabel>critical</AggregateLabel>
              <Dot>·</Dot>
            </>
          )}
          {group.warning > 0 && (
            <>
              <CountNumber tone="warning">{group.warning}</CountNumber>
              <AggregateLabel>warning</AggregateLabel>
              <Dot>·</Dot>
            </>
          )}
          <CountNumber tone="muted">{group.projects.length}</CountNumber>
          <AggregateLabel>{projectWord}</AggregateLabel>
        </Aggregates>
      </GroupHeader>

      <GroupBody>
        {group.projects.map((project) => (
          <StockIssueProjectCard
            key={project.firebaseId}
            project={project}
            productsById={productsById}
            onUpdateStatus={onUpdateStatus}
            onBatchUpdate={onBatchUpdate}
          />
        ))}
      </GroupBody>
    </GroupWrapper>
  );
}

// Section header: a tinted band that sits on the page, clearly not a card.
// The tint (slightly darker than page bg) gives it visual ground; the
// bottom rule anchors it to the group below. No accent tick, no collapse
// control — this is a label, not an interaction.
const GroupWrapper = styled.section`
  & + & {
    margin-top: 28px;
  }
`;

const GroupHeader = styled.div`
  display: flex;
  align-items: baseline;
  gap: 10px;
  width: 100%;
  padding: 10px 16px;
  margin-bottom: 10px;
  background: rgba(0, 0, 0, 0.06);
  border-bottom: 1px solid rgba(0, 0, 0, 0.14);
  border-radius: 2px 2px 0 0;
`;

const DistributorName = styled.h2`
  margin: 0;
  font-size: 15px;
  font-weight: ${FONTWEIGHT.heavy};
  color: ${COLORS.text};
  text-transform: uppercase;
  letter-spacing: 0.08em;
  line-height: 1;
`;

const Aggregates = styled.span`
  display: inline-flex;
  align-items: baseline;
  gap: 4px;
  font-size: ${FONTSIZE.xsmall};
  color: ${COLORS.stormtechGrey};
  text-transform: lowercase;
  letter-spacing: 0.02em;
`;

const CountNumber = styled.span`
  font-size: ${FONTSIZE.small};
  font-weight: ${FONTWEIGHT.heavy};
  color: ${({ tone }) =>
    tone === "critical"
      ? COLORS.alertRed
      : tone === "warning"
      ? COLORS.alertOrange
      : COLORS.text};
`;

const AggregateLabel = styled.span`
  font-size: ${FONTSIZE.xsmall};
  color: ${COLORS.stormtechGrey};
`;

const Dot = styled.span`
  color: ${COLORS.stormtechGrey};
  opacity: 0.5;
  font-size: ${FONTSIZE.xsmall};
`;

const GroupBody = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(420px, 1fr));
  gap: 12px;
`;
